import { Box, Calendar, Eye, Circle, CircleCheck, FileCog, FileJson, LoaderCircle, User } from "lucide-react"

import { DocumentOverlay } from "@/components/overlays/DocumentOverlay"
import { ReportTypeSelection } from "@/components/selections/ReportTypeSelection"
import { Button } from "@ui/button"
import { Label } from "@ui/label"
import { useUserCache } from "@hooks/useUserCache"
import { useUserSession } from "@session/UserSession"

import { CollectionItem, ContentObjectItem } from "@vertesia/common"

interface ReportTypeSelectionPanelProps {
    configuration: CollectionItem | undefined
    configurations: CollectionItem[]
    template: ContentObjectItem | undefined
    templates: ContentObjectItem[]
    loading: boolean
    handleTemplateDelete: () => void
    handleTemplateUpdate: (value: ContentObjectItem | undefined) => void
    handleTemplateConfigurationUpdate: (value: CollectionItem | undefined) => void
}
export function ReportTypeSelectionPanel({ configuration, configurations, template, templates, loading, handleTemplateDelete, handleTemplateUpdate, handleTemplateConfigurationUpdate }: Readonly<ReportTypeSelectionPanelProps>) {
    const { client } = useUserSession()
    const { getUserDisplayName } = useUserCache(client)

    return (
        <div className='flex flex-col gap-6 h-full min-h-0'>
            <ReportTypeSelection value={configuration} values={configurations} onChange={handleTemplateConfigurationUpdate} />
            {
                (!configuration)
                    ? (
                        <div className='flex flex-col items-center justify-center h-full gap-2'>
                            <FileCog className="h-16 w-16 text-gray-300" />
                            <div className='text-center'>
                                <h3 className="text-lg font-medium text-gray-900">
                                    No Report Type Selected
                                </h3>
                                <p className="text-gray-600">
                                    Select a Report Type first to browse related templates.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col gap-1 h-full min-h-0'>
                            <Label>Report Template</Label>
                            <div className="flex-1 min-h-0 overflow-y-scroll">
                                {
                                    loading ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="flex items-center justify-center h-full gap-2">
                                                <LoaderCircle className="h-6 w-6 animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                                <span className="text-xl">Loading documents.</span>
                                            </div>
                                        </div>
                                    ) : (
                                        templates.length === 0
                                            ? (
                                                <div className='flex flex-col items-center justify-center h-full gap-2'>
                                                    <FileJson className="h-16 w-16 text-gray-300" />
                                                    <div className='text-center'>
                                                        <h3 className="text-lg font-medium text-gray-900">
                                                            No Template Available for this configuration
                                                        </h3>
                                                        <p className="text-gray-600">
                                                            Contact your administrator if you think there is an issue.
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <ul className="space-y-2">
                                                    {
                                                        templates.map((doc) => (
                                                            <li key={doc.id} className={`flex flex-col gap-2 p-2 border rounded cursor-pointer ${template?.id === doc.id ? "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300" : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"}`} onClick={() => handleTemplateUpdate(doc)}>
                                                                <div className="flex flex-row gap-2 justify-between">
                                                                    <div className="flex flex-col gap-1">
                                                                        <div className="flex items-center gap-3 w-full">
                                                                            <div className="flex-shrink-0">
                                                                                {
                                                                                    template?.id === doc.id
                                                                                        ? <CircleCheck className="h-4 w-4 text-blue-500" />
                                                                                        : <Circle className="h-4 w-4 text-gray-400" />
                                                                                }
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <h4 className="font-semibold truncate" title={doc.name}>
                                                                                    {doc.name}
                                                                                </h4>
                                                                            </div>
                                                                            
                                                                        </div>
                                                                        <div className='flex flex-row justify-between items-center text-sm text-gray-500'>
                                                                            <div className='flex flex-row gap-6'>
                                                                                <div className="flex flex-row gap-1 items-center text-xs text-gray-400">
                                                                                    <User className='h-3 w-3' />
                                                                                    {getUserDisplayName(doc.created_by, "AI Report Generator")}
                                                                                </div>
                                                                                <div className="flex flex-row gap-1 items-center text-xs text-gray-400">
                                                                                    <Calendar className='h-3 w-3' />
                                                                                    {new Date(doc.created_at).toLocaleDateString()}
                                                                                </div>
                                                                                <div className="flex flex-row gap-1 items-center text-xs text-gray-400">
                                                                                    <Box className='h-3 w-3' />
                                                                                    {doc?.content?.type || "Unknown"}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <DocumentOverlay document={doc.id} className="!w-[48px]">
                                                                        <Button className='flex items-center bg-blue-500 text-white hover:bg-blue-600'>
                                                                            <Eye />
                                                                        </Button>
                                                                    </DocumentOverlay>
                                                                </div>
                                                                {
                                                                    doc?.tags && doc.tags.length > 0 &&
                                                                    <div>
                                                                        {
                                                                            doc?.tags?.map((tag, index) => (
                                                                                <span key={index} className="inline-block mt-1 mr-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                                                                                    {tag}
                                                                                </span>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                }
                                                            </li>
                                                        ))
                                                    }
                                                </ul>
                                            )
                                    )
                                }
                            </div>
                        </div>
                    )
            }
        </div>
    )
}
